import React from 'react';
import { Input } from 'reactstrap';
import { toast } from 'react-toastify';
import EmployeeService from '../../../services/Employee';
import PerformanceImprovementService from '../../../services/PerformanceImprovement';
import PolicyViolationsService from '../../../services/PolicyViolations';
import { FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';

export interface PerformanceImprovementImportData {
  employeesList: any[];
  policyViolationsList: any[];
}

export const loadPerformanceImprovementData = async (
  token: string,
  schoolId: string,
): Promise<PerformanceImprovementImportData> => {
  const employeesList = await loadEmployees(token, schoolId);
  const policyViolationsList = await loadPolicyViolations(token);

  return {
    employeesList,
    policyViolationsList,
  };
};

const loadEmployees = async (token: string, schoolId: string): Promise<any[]> => {
  try {
    const filteredOptions: FilteredOptionsInterface = {
      filters: [],
      options: createOptionsPage(createPagedMeta()),
    };
    filteredOptions.options.itemsPerPage = 100000;
    const result = await EmployeeService(token).listEmployeesBySchool(schoolId, '', filteredOptions);
    const employeesWithUsers = (result.data || []).filter((emp: any) => emp.user);
    return employeesWithUsers || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No employees found');
    return [];
  }
};

const loadPolicyViolations = async (token: string): Promise<any[]> => {
  try {
    const result = await PolicyViolationsService(token).list();
    return result.data || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No policy violations found');
    return [];
  }
};

export interface ProcessedPerformanceImprovement {
  userId: string;
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForPerformanceImprovementPlan: string;
  expectationsAndGoals: string;
  timelineAndDeadline: string;
  resourcesAndSupport: string;
  privateNotes: string;
  approved: boolean;
}

export interface ProcessingErrors {
  [key: string]: string;
}

export const processPerformanceImprovementImport = (
  importedLines: any[],
  formData: any[],
  employeesList: any[],
  policyViolationsList: any[],
): { performanceImprovements: ProcessedPerformanceImprovement[]; errors: ProcessingErrors } => {
  const performanceImprovements: ProcessedPerformanceImprovement[] = [];
  const errors: ProcessingErrors = {};

  for (let i = 0; i < importedLines.length; i++) {
    const line = importedLines[i];
    const formLine = formData[i] || {};

    const staffNameOrId =
      formLine['staff_name'] || formLine['staffname'] || line['staff_name'] || line['staffname'] || '';
    const dateOfIncident =
      formLine['date_of_incident'] ||
      formLine['dateofincident'] ||
      line['date_of_incident'] ||
      line['dateofincident'] ||
      '';
    const policyViolationsStr =
      formLine['policy_violations'] ||
      formLine['policyviolations'] ||
      line['policy_violations'] ||
      line['policyviolations'] ||
      '';
    const reasonForPlan =
      formLine['reason_for_plan'] ||
      formLine['reasonforplan'] ||
      line['reason_for_plan'] ||
      line['reasonforplan'] ||
      '';
    const expectationAndGoals =
      formLine['expectation_and_goals'] ||
      formLine['expectationandgoals'] ||
      formLine['expectations_and_goals'] ||
      formLine['expectationsandgoals'] ||
      line['expectation_and_goals'] ||
      line['expectationandgoals'] ||
      line['expectations_and_goals'] ||
      line['expectationsandgoals'] ||
      '';
    const timelineAndDeadline =
      formLine['timeline_and_deadline'] ||
      formLine['timelineanddeadline'] ||
      line['timeline_and_deadline'] ||
      line['timelineanddeadline'] ||
      '';
    const resourcesAndSupport =
      formLine['resources_and_support'] ||
      formLine['resourcesandsupport'] ||
      line['resources_and_support'] ||
      line['resourcesandsupport'] ||
      '';
    const privateNotes =
      formLine['private_notes'] || formLine['privatenotes'] || line['private_notes'] || line['privatenotes'] || '';

    let employee = employeesList.find((emp: any) => emp.user?.id === staffNameOrId);
    if (!employee) {
      employee = employeesList.find((emp: any) => {
        const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
        return fullName.toLowerCase() === String(staffNameOrId).toLowerCase().trim();
      });
    }

    if (!employee || !employee.user) {
      errors[`${i}.userId`] = `Staff member "${staffNameOrId}" not found`;
      continue;
    }

    const policyViolationNames = policyViolationsStr
      .split(/[,;]/)
      .map((name: string) => name.trim())
      .filter((name: string) => name.length > 0);

    const policyViolationIds: string[] = [];
    const notFoundViolations: string[] = [];

    for (const pvName of policyViolationNames) {
      const pv = policyViolationsList.find((pv: any) => pv.name.toLowerCase() === pvName.toLowerCase());
      if (pv) {
        policyViolationIds.push(pv.id);
      } else {
        notFoundViolations.push(pvName);
      }
    }

    if (notFoundViolations.length > 0) {
      errors[`${i}.policyViolations`] = `Policy violation${
        notFoundViolations.length > 1 ? 's' : ''
      } not found: ${notFoundViolations.join(', ')}`;
    }

    if (policyViolationIds.length === 0 && policyViolationNames.length === 0) {
      errors[`${i}.policyViolations`] = 'At least one policy violation is required';
    } else if (policyViolationIds.length === 0 && policyViolationNames.length > 0) {
      if (!errors[`${i}.policyViolations`]) {
        errors[`${i}.policyViolations`] = 'No valid policy violations found';
      }
    }

    if (!dateOfIncident) {
      errors[`${i}.date`] = 'Date of Incident is required';
    }

    if (!reasonForPlan.trim()) {
      errors[`${i}.reasonForPerformanceImprovementPlan`] = 'Reason For Plan is required';
    }

    if (!expectationAndGoals.trim()) {
      errors[`${i}.expectationsAndGoals`] = 'Expectation and Goals is required';
    }

    if (!timelineAndDeadline.trim()) {
      errors[`${i}.timelineAndDeadline`] = 'Timeline and deadline is required';
    }

    if (!resourcesAndSupport.trim()) {
      errors[`${i}.resourcesAndSupport`] = 'Resources and Support is required';
    }

    if (Object.keys(errors).some((key) => key.startsWith(`${i}.`))) {
      continue;
    }

    performanceImprovements.push({
      userId: employee.user.id,
      name: `${employee.user.firstName} ${employee.user.lastName}`,
      date: dateOfIncident,
      position: employee.title?.name || '',
      department: employee.department?.name || '',
      supervisor: employee.supervisor?.name || '',
      dateOfHire: employee.startDate || '2024-01-01',
      policyViolationIds,
      reasonForPerformanceImprovementPlan: reasonForPlan,
      expectationsAndGoals: expectationAndGoals,
      timelineAndDeadline,
      resourcesAndSupport,
      privateNotes,
      approved: true,
    });
  }

  return { performanceImprovements, errors };
};

export const submitPerformanceImprovementImport = async (
  token: string,
  schoolId: string,
  performanceImprovements: ProcessedPerformanceImprovement[],
): Promise<void> => {
  const service = PerformanceImprovementService(token);
  await service.create({ schoolId, performanceImprovements, isImport: true });
};

export const getPerformanceImprovementFieldConfig = (
  value: string,
  name: string,
  employeesList: any[],
  _policyViolationsList: any[],
): JSX.Element | null => {
  if (
    name.split('][')[1].toLocaleLowerCase() === 'staff_name]' ||
    name.split('][')[1].toLocaleLowerCase() === 'staffname]'
  ) {
    const selected = employeesList.find((emp: any) => {
      const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
      return fullName.toLowerCase() === String(value).toLowerCase().trim() || emp.user?.id === value;
    });
    const defaultValue = selected?.user?.id || value || '';
    return (
      <Input type="select" defaultValue={defaultValue} name={name}>
        <option value={''}></option>
        {employeesList.map((emp: any) => {
          const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
          return (
            <option key={emp.user?.id} value={emp.user?.id}>
              {fullName}
            </option>
          );
        })}
      </Input>
    );
  }

  if (
    name.split('][')[1].toLocaleLowerCase() === 'policy_violations]' ||
    name.split('][')[1].toLocaleLowerCase() === 'policyviolations]'
  ) {
    return <Input type="text" defaultValue={value} name={name} placeholder="Separate multiple with comma" />;
  }

  return null;
};
